/**
 * @file Photo Type Configuration API Types
 * @description 로딩 사진 타입 설정 API 타입 정의
 * @see docs/plans/loading/05-implementation.md - Section 31.1
 */

/**
 * 사진 타입 코드 (기본 9개 + 커스텀 8개 = 최대 17개)
 *
 * - 기본 유형: SAMPLE_BALE ~ EXTRA (9개, 중복 선택 불가)
 * - 커스텀 유형: CUSTOM_1 ~ CUSTOM_8 (8개, 자유 추가)
 */
export type PhotoTypeCode =
  | 'SAMPLE_BALE' // Sample Bale
  | 'EMPTY' // Empty
  | 'QUARTER_LOADED' // Quarter Loaded (1/4)
  | 'HALF_LOADED' // Half Loaded (1/2)
  | 'FULL_LOADED' // Full Loaded
  | 'ONE_DOOR_CLOSED' // One Door Closed
  | 'BOTH_DOORS_CLOSED' // Both Doors Closed
  | 'CLOSE_UP_SEAL' // Close Up Seal
  | 'EXTRA' // Extra
  | 'CUSTOM_1'
  | 'CUSTOM_2'
  | 'CUSTOM_3'
  | 'CUSTOM_4'
  | 'CUSTOM_5'
  | 'CUSTOM_6'
  | 'CUSTOM_7'
  | 'CUSTOM_8';

/**
 * 기본 사진 타입 코드 (7개)
 */
export type DefaultPhotoTypeCode = Exclude<PhotoTypeCode, `CUSTOM_${number}`>;

/**
 * 커스텀 사진 타입 코드 (8개)
 */
export type CustomPhotoTypeCode = Extract<PhotoTypeCode, `CUSTOM_${number}`>;

/**
 * 사진 타입 슬롯 DTO
 *
 * @description 백엔드 ExporterPhotoTypeConfigDto와 일치
 */
export interface PhotoTypeSlotDto {
  /** 슬롯 고유 ID */
  id: number;
  /** 사진 타입 코드 */
  typeCode: PhotoTypeCode;
  /** 제목 (1~20자, 영문/숫자/특수문자만) */
  title: string;
  /** 부제목 (0~30자, 선택) */
  subTitle: string | null;
  /** 표시 순서 (1~15) */
  displayOrder: number;
  /** 활성화 여부 */
  isActive: boolean;
  /** 커스텀 유형 여부 (기본 유형은 false) */
  isCustom: boolean;
  /** 생성일시 */
  createdAt: string;
  /** 수정일시 */
  updatedAt: string;
}

/**
 * 사진 타입 설정 조회 응답
 *
 * @description 백엔드 ExporterPhotoTypeConfigListResponseDto와 일치
 */
export interface GetPhotoTypeConfigResponse {
  /** 사진 타입 설정 목록 */
  photoTypeConfigs: PhotoTypeSlotDto[];
  /** 활성 슬롯 개수 */
  activeCount: number;
  /** 전체 슬롯 개수 */
  totalCount: number;
  /** 최대 허용 개수 (15) */
  maxCount: number;
  /** 최소 필수 개수 (7) */
  minRequired: number;
}

/**
 * 사진 타입 설정 수정 요청 항목
 */
export interface UpdatePhotoTypeConfigItem {
  /** 사진 타입 코드 */
  typeCode: PhotoTypeCode;
  /** 제목 (1~20자) */
  title: string;
  /** 부제목 (0~30자, 선택) */
  subTitle?: string | null;
  /** 표시 순서 (1~15) */
  displayOrder: number;
  /** 활성화 여부 */
  isActive: boolean;
}

/**
 * 사진 타입 설정 수정 요청
 *
 * @description 백엔드 UpdateExporterPhotoTypeConfigsDto와 일치
 */
export interface UpdatePhotoTypeConfigRequest {
  /** 수정할 사진 타입 설정 목록 */
  photoTypeConfigs: UpdatePhotoTypeConfigItem[];
}

/**
 * 기본 사진 타입별 기본 제목 매핑 (Title 입력란 기본값)
 */
export const DEFAULT_PHOTO_TYPE_TITLES: Record<DefaultPhotoTypeCode, string> = {
  SAMPLE_BALE: 'Sample Bale',
  EMPTY: 'Empty',
  QUARTER_LOADED: 'Quarter Loaded',
  HALF_LOADED: 'Half Loaded',
  FULL_LOADED: 'Full Loaded',
  ONE_DOOR_CLOSED: 'One Door Closed',
  BOTH_DOORS_CLOSED: 'Both Doors Closed',
  CLOSE_UP_SEAL: 'Close Up Seal',
  EXTRA: 'Extra',
};

/**
 * 기본 사진 타입별 짧은 레이블 매핑 (드롭다운 표시용)
 * @description Figma 디자인에 맞춘 축약형 레이블
 */
export const DEFAULT_PHOTO_TYPE_SHORT_LABELS: Record<DefaultPhotoTypeCode, string> = {
  SAMPLE_BALE: 'Sample',
  EMPTY: 'Empty',
  QUARTER_LOADED: '¼',
  HALF_LOADED: '½',
  FULL_LOADED: 'Full',
  ONE_DOOR_CLOSED: '1 Door',
  BOTH_DOORS_CLOSED: '2 Door',
  CLOSE_UP_SEAL: 'Seal',
  EXTRA: 'Extra',
};

/**
 * 기본 사진 타입 코드 목록 (순서 보장)
 */
export const DEFAULT_PHOTO_TYPE_CODES: DefaultPhotoTypeCode[] = [
  'SAMPLE_BALE',
  'EMPTY',
  'QUARTER_LOADED',
  'HALF_LOADED',
  'FULL_LOADED',
  'ONE_DOOR_CLOSED',
  'BOTH_DOORS_CLOSED',
  'CLOSE_UP_SEAL',
  'EXTRA',
];

/**
 * 편집 가능한 사진 타입 코드 목록 (CLOSE_UP_SEAL, EXTRA 제외)
 * @description Seal과 Extra는 고정 항목으로 이름 변경 불가
 */
export const EDITABLE_PHOTO_TYPE_CODES: DefaultPhotoTypeCode[] = [
  'SAMPLE_BALE',
  'EMPTY',
  'QUARTER_LOADED',
  'HALF_LOADED',
  'FULL_LOADED',
  'ONE_DOOR_CLOSED',
  'BOTH_DOORS_CLOSED',
];

export const CUSTOM_TYPE_PREFIX = 'CUSTOM_';

export const TYPE_CODE_REGEX = /^[A-Z][A-Z0-9_]{0,19}$/;

export const PHOTO_TYPE_CONFIG = {
  MIN_SLOTS: 7,
  MAX_SLOTS: 15,
  TITLE_MIN_LENGTH: 1,
  TITLE_MAX_LENGTH: 20,
  SUBTITLE_MAX_LENGTH: 50,
  TYPE_CODE_MIN_LENGTH: 1,
  TYPE_CODE_MAX_LENGTH: 20,
} as const;
